import { history } from "instantsearch.js/es/lib/routers";
import type { UiState } from "instantsearch.js";
import qs from "qs";

// Shared pure types, constants & helpers — single source of truth
import {
    type RangeAttribute,
    type RangeValue,
    type CuratedSearchFilters,
    type RangeMeta,
    RANGE_META,
    LEGACY_NAME_TO_ATTRIBUTE,
    sortIndexToFriendly,
    friendlyToSortIndex,
    parseUrlToRouteState,
    parseRouteStateFilters,
    createAlgoliaRoutingBlade,
    buildLandingPaginationUrl,
    resolveLandingDisplayPageFromUrl,
    landingDisplayPageToInstantSearchPage,
    instantSearchPageToLandingDisplayPage,
    isLandingExcerptPathname,
    resolveInstantSearchPageFromRoutePage,
    isHasPromotionRouteFlag,
} from "./algoliaRouting.core";

// Re-export for backward compatibility (consumers import from this file)
export type { RangeAttribute, RangeValue, CuratedSearchFilters, RangeMeta };
export {
    RANGE_META,
    LEGACY_NAME_TO_ATTRIBUTE,
    sortIndexToFriendly,
    friendlyToSortIndex,
    parseUrlToRouteState,
    buildLandingPaginationUrl,
    resolveLandingDisplayPageFromUrl,
    landingDisplayPageToInstantSearchPage,
    instantSearchPageToLandingDisplayPage,
    isLandingExcerptPathname,
    resolveInstantSearchPageFromRoutePage,
    isHasPromotionRouteFlag,
};

// Actif en local (VITE_DEVELOPMENT=true) et sur preprod (hostname pp.*).
// Contrôle les [Nav] logs (navigation/history events).
const DEBUG_ALGOLIA_ROUTING =
    import.meta.env.VITE_DEVELOPMENT === "true" ||
    (typeof window !== "undefined" &&
        window.location.hostname.startsWith("pp."));

// === FLAG NAVIGATION INERTIA ===
// Empêche le routing Algolia d'écrire dans l'historique pendant une navigation Inertia
// vers une AUTRE page (changement de pathname). Un popstate same-page (même pathname,
// query string différente — ex: bouton retour entre deux URLs /marketplace) ne doit
// PAS bloquer routeToState, sinon les filtres sont perdus (bug "163 résultats").
// Set by algoliaRouting.inertia.ts (Vue/Inertia bundle only).
let isInertiaNavigating = false;

export const setInertiaNavigating = (value: boolean): void => {
    isInertiaNavigating = value;
};

export const getInertiaNavigating = (): boolean => isInertiaNavigating;

// Pathnames de pages curées/spéciales visitées dans cette session.
// Alimenté par registerCuratedPathname() depuis Marketplace.vue au onMounted.
// Persisté en sessionStorage pour survivre aux hard navigations (window.location.href).
// Utilisé par le handler "before" pour forcer un hard reload quand on y revient
// via back/forward SPA (Inertia ne recharge pas la page, ce qui pollue l'état Algolia).
const CURATED_PATHNAMES_KEY = "__joinsteer_curated_pathnames";
const curatedPathnames = new Set<string>();
let isPendingPopstate = false;

// Restaure les pathnames depuis sessionStorage au démarrage du module
// (après une hard navigation, le module se réinitialise mais sessionStorage persiste).
if (typeof window !== "undefined") {
    try {
        const stored = JSON.parse(
            sessionStorage.getItem(CURATED_PATHNAMES_KEY) || "[]",
        );
        if (Array.isArray(stored)) {
            stored.forEach((p: string) => curatedPathnames.add(p));
        }
    } catch {
        // sessionStorage unavailable
    }
}

export const registerCuratedPathname = (pathname: string): void => {
    if (curatedPathnames.has(pathname)) return;
    curatedPathnames.add(pathname);
    try {
        const stored = JSON.parse(
            sessionStorage.getItem(CURATED_PATHNAMES_KEY) || "[]",
        );
        if (Array.isArray(stored) && !stored.includes(pathname)) {
            stored.push(pathname);
            sessionStorage.setItem(
                CURATED_PATHNAMES_KEY,
                JSON.stringify(stored),
            );
        }
    } catch {
        // sessionStorage unavailable
    }
};

export const hasCuratedPathname = (pathname: string): boolean =>
    curatedPathnames.has(pathname);

// === HISTORY TRACKER ===
// Reconstruit le stack du browser (non exposé nativement) via monkey-patch
// de pushState/replaceState + écoute de popstate.
// Permet de voir ce que sera le prochain back/forward.
const trackedHistory: string[] = [];
let trackedIndex = -1;

if (typeof window !== "undefined") {
    // Initialise avec l'entrée courante
    trackedHistory.push(window.location.href);
    trackedIndex = 0;

    const origPushState = window.history.pushState.bind(window.history);
    window.history.pushState = (state, title, url) => {
        origPushState(state, title, url);
        // Supprime les entrées "forward" après la position courante
        trackedHistory.splice(trackedIndex + 1);
        trackedHistory.push(window.location.href);
        trackedIndex = trackedHistory.length - 1;
    };

    const origReplaceState = window.history.replaceState.bind(window.history);
    window.history.replaceState = (state, title, url) => {
        origReplaceState(state, title, url);
        if (trackedIndex >= 0) {
            trackedHistory[trackedIndex] = window.location.href;
        }
    };
}

const getTrackedHistorySnapshot = () => {
    const p = (url: string) => {
        try {
            return new URL(url).pathname + new URL(url).search;
        } catch {
            return url;
        }
    };
    return trackedHistory.map((url, i) => {
        const label = p(url);
        if (i === trackedIndex) return `[${label}] ← current`;
        if (i === trackedIndex - 1) return `${label} ← back`;
        if (i === trackedIndex + 1) return `${label} ← forward`;
        return label;
    });
};

const logNav = (eventName: string, extra?: Record<string, unknown>) => {
    if (!DEBUG_ALGOLIA_ROUTING) return;
    console.log(
        "[Nav]",
        eventName,
        "| url:",
        window.location.href,
        "| browserHistory:",
        getTrackedHistorySnapshot(),
        ...(extra ? ["|", extra] : []),
    );
};

if (typeof window !== "undefined") {
    let currentPathname = window.location.pathname;

    // Phase CAPTURE : fire AVANT les handlers bubble (InstantSearch, Inertia).
    // Ne bloque routeToState que si le pathname change (navigation cross-page).
    window.addEventListener(
        "popstate",
        () => {
            // Retrouve la position dans notre stack après un back/forward
            const currentHref = window.location.href;
            const idx = trackedHistory.lastIndexOf(currentHref);
            if (idx !== -1) trackedIndex = idx;

            const crossPage = window.location.pathname !== currentPathname;
            if (crossPage) {
                setInertiaNavigating(true);
                isPendingPopstate = true;
            }
            logNav("popstate", {
                crossPage,
                isCuratedDestination: curatedPathnames.has(
                    window.location.pathname,
                ),
            });
        },
        true,
    );
}

/** Popstate flag consumed by algoliaRouting.inertia.ts (Vue/Inertia bundle only). */
export const consumePendingPopstate = (): boolean => {
    if (!isPendingPopstate) {
        return false;
    }
    isPendingPopstate = false;
    return true;
};

export const peekPendingPopstate = (): boolean => isPendingPopstate;

export const clearPendingPopstate = (): void => {
    isPendingPopstate = false;
};

// === TYPES (Vue-specific) ===
export interface RangeFiltersState {
    monthly_price: RangeValue;
    mileage: RangeValue;
    price: RangeValue;
    registration_at: RangeValue;
    weight: RangeValue;
    co2_emission: RangeValue;
    power: RangeValue;
    specification: string;
}

export interface CuratedSearchConfig {
    isActive: boolean;
    marketplaceEquivalentUrl: string | null;
    filters: CuratedSearchFilters;
}

// === ÉTAT DES FILTRES DE RANGE (partagé) ===
const defaultRangeFilters: RangeFiltersState = {
    ...Object.fromEntries(RANGE_META.map(({ attribute }) => [attribute, {}])),
    specification: "",
} as RangeFiltersState;

type StateListener = () => void;
const rangeFiltersListeners = new Set<StateListener>();
const curatedSearchListeners = new Set<StateListener>();

const notifyRangeFiltersState = (): void => {
    rangeFiltersListeners.forEach((listener) => listener());
};

const notifyCuratedSearchConfig = (): void => {
    curatedSearchListeners.forEach((listener) => listener());
};

/** Plain mutable state (no Vue). Blade bundle imports this directly. */
export const rangeFiltersState: { value: RangeFiltersState } = {
    value: { ...defaultRangeFilters },
};

export const subscribeRangeFiltersState = (
    listener: StateListener,
): (() => void) => {
    rangeFiltersListeners.add(listener);
    return () => rangeFiltersListeners.delete(listener);
};

const setRangeFiltersState = (next: RangeFiltersState): void => {
    rangeFiltersState.value = next;
    notifyRangeFiltersState();
};

const mutateRangeFiltersState = (
    mutator: (state: RangeFiltersState) => void,
): void => {
    mutator(rangeFiltersState.value);
    notifyRangeFiltersState();
};

/**
 * Remplace intégralement rangeFiltersState depuis des filtres parsés.
 * Source unique pour toutes les fonctions d'initialisation.
 */
const syncRangeFiltersFromParsed = (filters: CuratedSearchFilters) => {
    const ranges = Object.fromEntries(
        RANGE_META.map(({ attribute, minKey, maxKey }) => [
            attribute,
            {
                min: minKey
                    ? (filters[minKey] as number | undefined)
                    : undefined,
                max: filters[maxKey] as number | undefined,
            },
        ]),
    ) as Omit<RangeFiltersState, "specification">;
    setRangeFiltersState({
        ...ranges,
        specification:
            (filters as any).specification ??
            rangeFiltersState.value.specification ??
            "",
    });
};

/**
 * Initialise rangeFiltersState depuis une URL.
 * Doit être appelé AVANT le rendu des composants pour que rangeFilters soit correct.
 */
export const initRangeFiltersForSSR = (urlString: string) => {
    const routeState = parseUrlToRouteState(urlString);
    syncRangeFiltersFromParsed(parseRouteStateFilters(routeState));
    if (routeState.specification) {
        mutateRangeFiltersState((state) => {
            state.specification = routeState.specification;
        });
    }
};

/**
 * Applique les filtres curatedSearch sur rangeFiltersState (pour SSR).
 * Les valeurs curées prennent priorité ; les champs non définis conservent leur valeur URL.
 * À appeler APRÈS initRangeFiltersForSSR.
 */
export const applyCuratedSearchFiltersForSSR = (
    filters: CuratedSearchFilters,
) => {
    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const minVal = minKey
            ? (filters[minKey] as number | undefined)
            : undefined;
        const maxVal = filters[maxKey] as number | undefined;
        if (minVal !== undefined || maxVal !== undefined) {
            mutateRangeFiltersState((state) => {
                state[attribute] = {
                    min: minVal ?? state[attribute].min,
                    max: maxVal ?? state[attribute].max,
                };
            });
        }
    }
};

// === CONSTRUCTION DE LA CHAÎNE DE FILTRES TYPESENSE ===

const escapeTypesenseValue = (v: string): string =>
    /[(),\[\]]|&&|\|\|/.test(v) ? `\`${v.replace(/`/g, "``")}\`` : `"${v}"`;

const buildRangeFilter = (
    attribute: string,
    range: RangeValue,
): string | null => {
    const { min, max } = range;
    if (min !== undefined && max !== undefined)
        return `${attribute}:>=${min} && ${attribute}:<=${max}`;
    if (min !== undefined) return `${attribute}:>=${min}`;
    if (max !== undefined) return `${attribute}:<=${max}`;
    return null;
};

/**
 * Construit la chaîne de filtres Typesense depuis un CuratedSearchFilters.
 * Couvre les ranges numériques, refinementList et menu.
 */
export const buildFiltersString = (filters: CuratedSearchFilters): string => {
    const parts: string[] = [];

    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const filter = buildRangeFilter(attribute, {
            min: minKey ? (filters[minKey] as number | undefined) : undefined,
            max: filters[maxKey] as number | undefined,
        });
        if (filter) parts.push(filter);
    }

    if (filters.refinementList) {
        for (const [attribute, values] of Object.entries(
            filters.refinementList,
        )) {
            if (values?.length > 0) {
                // Codes pays indexés en majuscules côté Typesense
                const normalized =
                    attribute === "supplier_country"
                        ? values.map((v) => String(v).trim().toUpperCase())
                        : values;
                parts.push(
                    `${attribute}:[${normalized
                        .map(escapeTypesenseValue)
                        .join(", ")}]`,
                );
            }
        }
    }

    if (filters.menu) {
        for (const [attribute, value] of Object.entries(filters.menu)) {
            if (value)
                parts.push(`${attribute}:=${escapeTypesenseValue(value)}`);
        }
    }

    return parts.join(" && ");
};

/**
 * Parse une URL vers un UiState complet pour InstantSearch SSR.
 * Les filtres curatedSearch sont fusionnés en priorité basse (l'URL gagne).
 */
export const parseUrlToUiState = (
    urlString: string,
    indexName: string,
    curatedSearchFilters?: CuratedSearchFilters,
): UiState => {
    const routeState = parseUrlToRouteState(urlString);
    const urlFilters = parseRouteStateFilters(routeState);

    let mergedFilters: CuratedSearchFilters = {
        ...urlFilters,
        menu: routeState.menu,
        refinementList: routeState.refinementList,
    };

    if (curatedSearchFilters) {
        mergedFilters = {
            ...curatedSearchFilters,
            ...mergedFilters,
            // Merge profond : URL a priorité par clé
            refinementList: {
                ...curatedSearchFilters.refinementList,
                ...(routeState.refinementList || {}),
            },
            menu: { ...curatedSearchFilters.menu, ...(routeState.menu || {}) },
        };
    }

    const uiState: UiState = {
        [indexName]: {
            query: routeState.query || "",
            menu: mergedFilters.menu || {},
            refinementList: mergedFilters.refinementList || {},
        },
    };

    if (routeState.sortBy) {
        (uiState[indexName] as any).sortBy = friendlyToSortIndex(
            routeState.sortBy,
            indexName,
        );
    }

    const filtersString = buildFiltersString(mergedFilters);
    if (filtersString) {
        (uiState[indexName] as any).configure = { filters: filtersString };
    }

    if (routeState.page !== undefined && routeState.page !== "") {
        const rawPage = Number(routeState.page);
        const pathname = new URL(urlString, "http://localhost").pathname;
        (uiState[indexName] as any).page =
            resolveInstantSearchPageFromRoutePage(rawPage, pathname);
    }

    return uiState;
};

// === BRIDGE INSTANTSEARCH ===
export type SetUiStateFn = (
    uiState: UiState | ((previousUiState: UiState) => UiState),
) => void;

let instantSearchSetUiState: SetUiStateFn | null = null;
let instantSearchRefresh: (() => void) | null = null;
let currentUiState: UiState | null = null;
let currentIndexName: string | null = null;
let specificationTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Registers the InstantSearch instance so range/specification changes can force
 * a new search (configure.filters is not owned by a widget uiState slice on Blade).
 */
export const registerInstantSearchForRangeFilters = (search: {
    refresh: () => void;
    helper?: { search: () => void } | null;
}) => {
    instantSearchRefresh = () => {
        if (search.helper && typeof search.helper.search === "function") {
            search.helper.search();
            return;
        }
        search.refresh();
    };
};

/**
 * Initialise le bridge avec InstantSearch.
 * À appeler depuis handleAlgoliaStateChange du Marketplace.
 */
export const initInstantSearchBridge = (
    setUiState: SetUiStateFn,
    uiState: UiState,
    indexName: string,
) => {
    instantSearchSetUiState = setUiState;
    currentUiState = uiState;
    currentIndexName = indexName;
};

/**
 * Synchronise manuellement l'URL avec les filtres de range (fallback quand le bridge n'est pas initialisé)
 */
const syncRangeFiltersToUrl = () => {
    if (typeof window === "undefined") return;

    const state = rangeFiltersState.value;
    const url = new URL(window.location.href);

    const params: [string, string | undefined][] = [];
    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const range = state[attribute];
        if (minKey) params.push([minKey, range.min?.toString()]);
        params.push([maxKey, range.max?.toString()]);
    }
    params.push(["specification", state.specification?.trim() || undefined]);

    params.forEach(([key, value]) => {
        value !== undefined && value !== ""
            ? url.searchParams.set(key, value)
            : url.searchParams.delete(key);
    });

    // Décoder les crochets pour garder des URLs lisibles
    window.history.replaceState(
        {},
        "",
        url.toString().replace(/%5B/g, "[").replace(/%5D/g, "]"),
    );
};

/**
 * Sync URL, push configure.filters from rangeFiltersState, then re-run search.
 * Uses functional setUiState so facet uiState is preserved (no stale snapshot).
 */
const triggerUiStateUpdate = () => {
    const filtersString = getFiltersFromRangeState();

    if (instantSearchSetUiState && currentIndexName) {
        const indexName = currentIndexName;
        instantSearchSetUiState(
            (prevState) =>
                ({
                    ...prevState,
                    [indexName]: {
                        ...prevState[indexName],
                        configure: {
                            filters: filtersString || undefined,
                        },
                    },
                }) as UiState,
        );
    }

    syncRangeFiltersToUrl();
    instantSearchRefresh?.();
};

// === MUTATIONS DES FILTRES DE RANGE ===

/** Set from marketplace-search.ts when filters should redirect to /marketplace. */
let onUserFilterInteraction: (() => void) | null = null;

export const setOnUserFilterInteraction = (fn: (() => void) | null): void => {
    onUserFilterInteraction = fn;
};

export const updateRangeFilter = (
    attribute: keyof Omit<RangeFiltersState, "specification">,
    value: RangeValue,
) => {
    onUserFilterInteraction?.();
    mutateRangeFiltersState((state) => {
        state[attribute] = value;
    });
    triggerUiStateUpdate();
};

export const updateSpecificationFilter = (value: string) => {
    onUserFilterInteraction?.();
    mutateRangeFiltersState((state) => {
        state.specification = value;
    });
    if (specificationTimeout) clearTimeout(specificationTimeout);
    specificationTimeout = setTimeout(() => triggerUiStateUpdate(), 200);
};

export const clearRangeFilter = (
    attribute: keyof Omit<RangeFiltersState, "specification">,
) => {
    onUserFilterInteraction?.();
    mutateRangeFiltersState((state) => {
        state[attribute] = {};
    });
    triggerUiStateUpdate();
};

export const clearSpecificationFilter = () => {
    onUserFilterInteraction?.();
    mutateRangeFiltersState((state) => {
        state.specification = "";
    });
    triggerUiStateUpdate();
};

/** Cancels a pending debounced specification search (e.g. before make change reset). */
export const cancelSpecificationFilterDebounce = () => {
    if (specificationTimeout) {
        clearTimeout(specificationTimeout);
        specificationTimeout = null;
    }
};

/** Cleared from uiState.refinementList when the user switches brand (Vue resetRefinementList). */
export const MAKE_DEPENDENT_REFINEMENT_FACETS = [
    "model",
    "options",
    "reference_exterior_color",
    "reference_interior_color",
] as const;

export function resetRefinementListInUiState(
    uiState: UiState,
    indexName: string,
    facets: readonly string[],
): void {
    const indexState = (uiState[indexName] ??= {
        query: "",
        menu: {},
        refinementList: {},
    }) as Record<string, unknown>;

    const refinementList = (indexState.refinementList ??= {}) as Record<
        string,
        string[]
    >;

    for (const facet of facets) {
        delete refinementList[facet];
    }
}

export function normalizeMakeSelection(
    indexState:
        | {
              menu?: { make?: string };
              refinementList?: { make?: string[] };
          }
        | undefined,
): string[] {
    const fromList = indexState?.refinementList?.make;
    if (Array.isArray(fromList) && fromList.length > 0) {
        return fromList;
    }
    const fromMenu = indexState?.menu?.make;
    if (typeof fromMenu === "string" && fromMenu.length > 0) {
        return [fromMenu];
    }
    return [];
}

export function makeSelectionSignature(makes: string[]): string {
    return [...makes].sort().join("|");
}

/**
 * When the selected make(s) change, drop dependent refinementList facets and the
 * configure-based specification filter (parity with Marketplace.vue handleAlgoliaStateChange).
 */
export function applyMakeChangeDependentResets(
    uiState: UiState,
    indexName: string,
    previousMake: string | string[] | undefined,
    currentMake: string | string[] | undefined,
): boolean {
    const previousMakes = Array.isArray(previousMake)
        ? previousMake
        : previousMake
        ? [previousMake]
        : [];
    const currentMakes = Array.isArray(currentMake)
        ? currentMake
        : currentMake
        ? [currentMake]
        : [];

    if (
        makeSelectionSignature(previousMakes) ===
        makeSelectionSignature(currentMakes)
    ) {
        return false;
    }

    resetRefinementListInUiState(
        uiState,
        indexName,
        MAKE_DEPENDENT_REFINEMENT_FACETS,
    );
    cancelSpecificationFilterDebounce();
    mutateRangeFiltersState((state) => {
        state.specification = "";
    });

    const indexState = (uiState[indexName] ??= {}) as Record<string, unknown>;
    const filtersString = getFiltersFromRangeState();
    indexState.configure = {
        filters: filtersString || undefined,
    };

    return true;
}

/** Efface un filtre de range par son nom legacy (ex: "mileageRange"). */
export const clearRangeFilterByLegacyName = (legacyName: string) => {
    const attribute = LEGACY_NAME_TO_ATTRIBUTE[legacyName];
    if (attribute) clearRangeFilter(attribute);
};

export const clearAllRangeFilters = () => {
    setRangeFiltersState({ ...defaultRangeFilters });
};

/**
 * Plages et finition « de base » : vides, sauf celles imposées par la page (landing ou
 * recherche curée), que « Réinitialiser » / « Effacer » ne doivent pas retirer.
 */
const baselineRangeFilters = (): RangeFiltersState => {
    const config = curatedSearchConfig.value;
    const filters = config.isActive ? config.filters ?? {} : {};
    const ranges = Object.fromEntries(
        RANGE_META.map(({ attribute, minKey, maxKey }) => [
            attribute,
            {
                min: minKey ? (filters[minKey] as number | undefined) : undefined,
                max: filters[maxKey] as number | undefined,
            },
        ]),
    ) as Omit<RangeFiltersState, "specification">;
    return { ...ranges, specification: "" };
};

/** Vrai si une plage ou la finition diffère de l'état de base (un filtre à effacer). */
export const hasActiveRangeOrSpecificationFilter = (): boolean => {
    const state = rangeFiltersState.value;
    if (state.specification?.trim()) return true;
    const baseline = baselineRangeFilters();
    return RANGE_META.some(({ attribute }) => {
        const current = state[attribute] ?? {};
        const base = baseline[attribute];
        return current.min !== base.min || current.max !== base.max;
    });
};

/**
 * Remet plages et finition à l'état de base, met à jour configure.filters et l'URL, puis
 * relance la recherche. Utilisé par « Réinitialiser » et « Effacer » (panneau Filtrer).
 */
export const resetRangeAndSpecificationFilters = () => {
    cancelSpecificationFilterDebounce();
    setRangeFiltersState(baselineRangeFilters());
    triggerUiStateUpdate();
};

/**
 * Applique les filtres curés sur rangeFiltersState (côté client, overlay).
 * Seuls les attributs définis dans filters sont mis à jour ; les autres sont préservés.
 * Appelé par setCuratedSearchConfig pour overlay les ranges.
 */
const initRangeFiltersFromCuratedConfig = (filters: CuratedSearchFilters) => {
    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const minVal = minKey
            ? (filters[minKey] as number | undefined)
            : undefined;
        const maxVal = filters[maxKey] as number | undefined;
        if (minVal !== undefined || maxVal !== undefined) {
            mutateRangeFiltersState((state) => {
                state[attribute] = { min: minVal, max: maxVal };
            });
        }
    }
};

// === CONFIG CURÉE ===
const defaultConfig: CuratedSearchConfig = {
    isActive: false,
    marketplaceEquivalentUrl: null,
    filters: {},
};

export const curatedSearchConfig: { value: CuratedSearchConfig } = {
    value: { ...defaultConfig },
};

export const subscribeCuratedSearchConfig = (
    listener: StateListener,
): (() => void) => {
    curatedSearchListeners.add(listener);
    return () => curatedSearchListeners.delete(listener);
};

export const setCuratedSearchConfig = (
    config: Partial<CuratedSearchConfig>,
) => {
    curatedSearchConfig.value = { ...defaultConfig, ...config, isActive: true };
    notifyCuratedSearchConfig();
    if (config.filters) initRangeFiltersFromCuratedConfig(config.filters);
};

export const clearCuratedSearchConfig = () => {
    curatedSearchConfig.value = { ...defaultConfig };
    notifyCuratedSearchConfig();
};

export const cleanupRoutingState = () => {
    clearCuratedSearchConfig();
    clearAllRangeFilters();
    if (specificationTimeout) {
        clearTimeout(specificationTimeout);
        specificationTimeout = null;
    }
    instantSearchSetUiState = null;
    currentUiState = null;
    currentIndexName = null;
};

const buildRouteState = (
    uiState: UiState,
    indexName: string,
    includeCuratedDefaults: boolean,
): Record<string, any> => {
    // ais-sort-by can express the active sort in two ways:
    // 1. Property: uiState["vehicles"].sortBy = "vehicles/sort/monthly_price:asc"
    // 2. Key rotation: uiState["vehicles/sort/monthly_price:asc"] = { menu: … }
    // We handle both.
    const activeIndexName =
        Object.keys(uiState).find(
            (key) => key === indexName || key.startsWith(indexName + "/sort/"),
        ) ?? indexName;
    const indexUiState = uiState[activeIndexName] || {};
    const routeState: Record<string, any> = {};

    const sortByValue =
        (indexUiState as any).sortBy ??
        (activeIndexName !== indexName ? activeIndexName : undefined);
    if (sortByValue && sortByValue !== indexName) {
        const friendly = sortIndexToFriendly(sortByValue, indexName);
        if (friendly) routeState.sortBy = friendly;
    }

    if (indexUiState.query) routeState.query = indexUiState.query;

    const config = curatedSearchConfig.value;
    const curatedFilters =
        config.isActive && config.filters ? config.filters : null;
    const curatedMenu = curatedFilters?.menu ?? {};
    const curatedRL = curatedFilters?.refinementList ?? {};

    // --- menu ---
    if (includeCuratedDefaults) {
        const menu = { ...curatedMenu, ...(indexUiState.menu || {}) };
        if (Object.keys(menu).length > 0) routeState.menu = menu;
    } else {
        const filteredMenu = Object.fromEntries(
            Object.entries(indexUiState.menu ?? {}).filter(
                ([key, val]) => val !== curatedMenu[key],
            ),
        );
        if (Object.keys(filteredMenu).length > 0)
            routeState.menu = filteredMenu;
    }

    // --- refinementList ---
    // has_promotion is a flat URL flag — strip it from nested refinementList.
    if (includeCuratedDefaults) {
        const rl = { ...curatedRL, ...(indexUiState.refinementList || {}) };
        const { has_promotion: _promo, ...rlWithoutPromo } = rl;
        if (Object.keys(rlWithoutPromo).length > 0) {
            routeState.refinementList = rlWithoutPromo;
        }
    } else {
        const filteredRL = Object.fromEntries(
            Object.entries(indexUiState.refinementList ?? {}).filter(
                ([key, val]) => {
                    if (key === "has_promotion") return false;
                    const curatedVal = curatedRL[key];
                    if (!curatedVal) return true;
                    const a = [...(val as string[])].sort();
                    const b = [...(curatedVal as string[])].sort();
                    return (
                        a.length !== b.length || a.some((v, i) => v !== b[i])
                    );
                },
            ),
        );
        if (Object.keys(filteredRL).length > 0)
            routeState.refinementList = filteredRL;
    }

    // --- ranges ---
    const ranges = rangeFiltersState.value;
    for (const { attribute, minKey, maxKey } of RANGE_META) {
        const range = ranges[attribute];
        const curatedMin =
            minKey && curatedFilters
                ? (curatedFilters[minKey] as number | undefined)
                : undefined;
        const curatedMax = curatedFilters
            ? (curatedFilters[maxKey] as number | undefined)
            : undefined;

        if (includeCuratedDefaults) {
            if (minKey && range.min !== undefined)
                routeState[minKey] = range.min;
            if (range.max !== undefined) routeState[maxKey] = range.max;
        } else {
            if (minKey && range.min !== undefined && range.min !== curatedMin)
                routeState[minKey] = range.min;
            if (range.max !== undefined && range.max !== curatedMax)
                routeState[maxKey] = range.max;
        }
    }
    if (ranges.specification?.trim())
        routeState.specification = ranges.specification;

    // Page — serialize any page > 0 (page 0 = display page 1 = default, no param needed)
    if ((indexUiState as any).page > 0) {
        routeState.page = (indexUiState as any).page;
    }

    if (indexUiState.refinementList?.has_promotion?.includes("true")) {
        routeState.has_promotion = true;
    }

    return routeState;
};

/**
 * UiState → URL /marketplace complète.
 * Inclut les defaults curés car sur /marketplace ils ne sont plus implicites.
 */
/** Encode ce qui casserait une valeur de query string (`%`, `#`, `&`, `=`, `+`, `?`), rien d'autre. */
export const encodeUrlBreakingChars = (value: string): string =>
    value.replace(/[%#&=+?]/g, (char) => encodeURIComponent(char));

export const uiStateToMarketplaceUrl = (
    uiState: UiState,
    indexName: string,
    options: {
        /** Facettes à retirer de l'URL, y compris quand la page les impose (ex. marque d'une landing). */
        omitFacets?: readonly string[];
        /** Repartir de la première page (la pagination d'une landing ne vaut pas sur /marketplace). */
        resetPage?: boolean;
        /**
         * Facettes à retirer seulement de `refinementList` (le `menu` est gardé), y compris quand la page les
         * impose : ex. les marques d'une page dynamique quand le visiteur choisit une autre marque.
         */
        omitRefinementList?: readonly string[];
    } = {},
): string => {
    const routeState = buildRouteState(uiState, indexName, true);
    for (const facet of options.omitRefinementList ?? []) {
        if (!routeState.refinementList) break;
        delete routeState.refinementList[facet];
        if (Object.keys(routeState.refinementList).length === 0) delete routeState.refinementList;
    }
    for (const facet of options.omitFacets ?? []) {
        for (const key of ["menu", "refinementList"] as const) {
            if (!routeState[key]) continue;
            delete routeState[key][facet];
            if (Object.keys(routeState[key]).length === 0) delete routeState[key];
        }
        // La finition saisie en texte est sérialisée à la racine (`specification=GT3`).
        delete routeState[facet];
    }
    if (options.resetPage) delete routeState.page;
    // Format lisible gardé (`menu[make]=Aston Martin`) ; seuls les caractères qui couperaient
    // l'URL sont encodés dans les valeurs : `#`, `&` ou `=` d'une recherche ne partent plus
    // dans le fragment et n'ajoutent pas de filtre.
    const queryString = qs.stringify(routeState, {
        addQueryPrefix: true,
        arrayFormat: "indices",
        encoder: (value: unknown, _defaultEncoder: unknown, _charset: unknown, type: string) =>
            type === "value" ? encodeUrlBreakingChars(String(value)) : String(value),
    });
    return `/marketplace${queryString}`;
};

// === HELPERS FILTRES ===

/** Retourne les filtres Typesense depuis l'état de range (inclut specification). */
export const getFiltersFromRangeState = (): string => {
    const state = rangeFiltersState.value;
    const parts: string[] = [];

    if (state.specification?.trim()) {
        parts.push(`specification:${state.specification.trim()}*`);
    }

    for (const { attribute } of RANGE_META) {
        const filter = buildRangeFilter(attribute, state[attribute]);
        if (filter) parts.push(filter);
    }

    return parts.join(" && ");
};

// === FILTRES SANS FACETTE ===
/**
 * Attributs filtrables depuis l'URL (recherches construites, alertes, liens de pages dynamiques)
 * mais sans widget sur la marketplace : ni facette, ni chip, ni compteur de filtres.
 */
export const HIDDEN_REFINEMENT_ATTRIBUTES = ["supplier_country"] as const;

/** Valeurs des filtres sans facette lues dans l'URL de /marketplace (Blade). */
export const hiddenRefinementsState: { value: Record<string, string[]> } = {
    value: {},
};

/**
 * Retire les filtres sans facette d'un refinementList d'URL et les renvoie à part.
 */
export const extractHiddenRefinements = (
    refinementList: unknown,
): {
    hidden: Record<string, string[]>;
    rest: Record<string, string[]> | undefined;
} => {
    if (!refinementList || typeof refinementList !== "object") {
        return { hidden: {}, rest: undefined };
    }

    const hidden: Record<string, string[]> = {};
    const rest: Record<string, string[]> = {};

    for (const [attribute, values] of Object.entries(
        refinementList as Record<string, unknown>,
    )) {
        const list = (Array.isArray(values) ? values : [values])
            .filter((v) => v !== undefined && v !== null && v !== "")
            .map(String);

        if (
            (HIDDEN_REFINEMENT_ATTRIBUTES as readonly string[]).includes(
                attribute,
            )
        ) {
            if (list.length > 0) hidden[attribute] = list;
        } else {
            rest[attribute] = values as string[];
        }
    }

    return { hidden, rest };
};

const getHiddenRefinementsFilterString = (): string =>
    buildFiltersString({ refinementList: hiddenRefinementsState.value });

/** Retourne tous les filtres (range + curated search facets + filtres sans facette) pour ais-configure. */
export const getAllFiltersString = (): string => {
    const rangeFiltersString = getFiltersFromRangeState();
    const hiddenFiltersString = getHiddenRefinementsFilterString();

    const config = curatedSearchConfig.value;
    if (!config.isActive || !config.filters) {
        return [rangeFiltersString, hiddenFiltersString]
            .filter(Boolean)
            .join(" && ");
    }

    const curatedFacetsString = buildFiltersString({
        refinementList: config.filters.refinementList,
        menu: config.filters.menu,
    });

    return [rangeFiltersString, curatedFacetsString, hiddenFiltersString]
        .filter(Boolean)
        .join(" && ");
};

// === STATE MAPPING ===
export const createStateMapping = (indexName: string) => ({
    stateToRoute(uiState: UiState): Record<string, any> {
        const routeState = buildRouteState(uiState, indexName, false);
        return routeState;
    },

    routeToState(routeState: Record<string, any>): UiState {
        // Pendant un popstate cross-page, la config curée de la nouvelle page n'est pas
        // encore en place. On retourne un état vide EXPLICITE (query/menu/refinementList
        // vides) pour qu'InstantSearch nettoie les widgets périmés, et on clear les
        // ranges pour que ais-configure ne garde pas les anciens filtres.
        if (isInertiaNavigating) {
            clearAllRangeFilters();
            return {
                [indexName]: {
                    query: "",
                    menu: {},
                    refinementList: {},
                },
            };
        }

        const config = curatedSearchConfig.value;
        const urlRangeFilters = parseRouteStateFilters(routeState);

        let menu = routeState.menu || {};
        let refinementList = { ...(routeState.refinementList || {}) };
        delete refinementList.has_promotion;
        let rangeFilters: CuratedSearchFilters = { ...urlRangeFilters };

        if (config.isActive && config.filters) {
            // Filtrer les undefined de urlRangeFilters pour ne pas écraser les
            // defaults curés (ex: price_min: 50000) avec des valeurs absentes de l'URL.
            const definedUrlFilters = Object.fromEntries(
                Object.entries(urlRangeFilters).filter(
                    ([, v]) => v !== undefined,
                ),
            ) as CuratedSearchFilters;
            rangeFilters = { ...config.filters, ...definedUrlFilters };
            if (config.filters.refinementList)
                refinementList = {
                    ...config.filters.refinementList,
                    ...refinementList,
                };
            if (config.filters.menu) menu = { ...config.filters.menu, ...menu };
        }

        if (isHasPromotionRouteFlag(routeState.has_promotion)) {
            refinementList = {
                ...refinementList,
                has_promotion: ["true"],
            };
        }

        syncRangeFiltersFromParsed({ ...rangeFilters, menu, refinementList });

        const uiState: UiState = {
            [indexName]: {
                query: routeState.query || "",
                menu,
                refinementList,
            },
        };

        if (routeState.sortBy) {
            (uiState[indexName] as any).sortBy = friendlyToSortIndex(
                routeState.sortBy,
                indexName,
            );
        }

        const filtersString = buildFiltersString({
            ...rangeFilters,
            menu,
            refinementList,
        });
        if (filtersString) {
            (uiState[indexName] as any).configure = { filters: filtersString };
        }

        if (routeState.page !== undefined && routeState.page !== "") {
            const rawPage = Number(routeState.page);
            const pathname =
                typeof window !== "undefined" ? window.location.pathname : "";
            (uiState[indexName] as any).page =
                resolveInstantSearchPageFromRoutePage(rawPage, pathname);
        }

        return uiState;
    },
});

/**
 * Restores curated / special-page InstantSearch state when hydration drifts from the
 * SEO URL defaults (same guard as Marketplace.vue handleAlgoliaStateChange).
 *
 * @returns true when state was corrected (caller should return early from onStateChange)
 */
export const restoreCuratedUiStateIfDrifted = (
    uiState: UiState,
    setUiState: SetUiStateFn,
    indexName: string,
    compareRouteStates = false,
): boolean => {
    const config = curatedSearchConfig.value;
    if (!config.isActive || !config.filters || typeof window === "undefined") {
        return false;
    }

    const routeStateFromUrl = parseUrlToRouteState(window.location.href);
    const stateMapping = createStateMapping(indexName);
    const expectedUiState = stateMapping.routeToState(routeStateFromUrl);
    const expectedIndex = expectedUiState[indexName] as Record<string, unknown>;
    const currentIndex = uiState[indexName] as Record<string, unknown>;

    if (!expectedIndex) {
        return false;
    }

    // Marketplace.vue's own comparison (once hydrated): removing a curated chip or
    // resetting serializes to {} like the curated state, so it is not restored.
    if (compareRouteStates) {
        if (
            JSON.stringify(stateMapping.stateToRoute(uiState)) ===
            JSON.stringify(stateMapping.stateToRoute(expectedUiState))
        ) {
            return false;
        }
        setUiState(expectedUiState);
        return true;
    }

    // stateToRoute strips curated defaults (includeCuratedDefaults: false), so an empty
    // drifted uiState and the correct curated uiState both serialize to {} — compare
    // the InstantSearch index state directly, especially configure.filters (Vehicle/Index
    // filters exclusively through ais-configure / getAllFiltersString).
    const expectedFilters = (expectedIndex.configure as { filters?: string })
        ?.filters;
    const currentFilters = (currentIndex?.configure as { filters?: string })
        ?.filters;

    const sameMenu =
        JSON.stringify(expectedIndex.menu ?? {}) ===
        JSON.stringify(currentIndex?.menu ?? {});
    const sameRefinementList =
        JSON.stringify(expectedIndex.refinementList ?? {}) ===
        JSON.stringify(currentIndex?.refinementList ?? {});
    const sameConfigureFilters = expectedFilters === currentFilters;
    const samePage = (expectedIndex.page ?? 0) === (currentIndex?.page ?? 0);

    if (sameMenu && sameRefinementList && sameConfigureFilters && samePage) {
        return false;
    }

    setUiState(expectedUiState);
    return true;
};

type CuratedPageStateChangeOptions = {
    indexName: string;
    isCuratedSearchPage: boolean;
    shouldRedirectFiltersToMarketplace: boolean;
    getHasPendingUserFacetRefine: () => boolean;
    clearHasPendingUserFacetRefine: () => void;
    /** True shortly after a user filter change — blocks curated restore until routing syncs. */
    getUserFilterGraceActive?: () => boolean;
    getIsCuratedReady: () => boolean;
    isCuratedRedirectPending: () => boolean;
    setCuratedRedirectPending: (value: boolean) => void;
    /**
     * Curated SEO search (Vehicle/Index mirror): once ready, the drift guard compares
     * route states like Marketplace.vue, a make change drops the dependent facets
     * (resetRefinementList) and removing a curated range also leaves for /marketplace.
     */
    vehicleIndexParity?: boolean;
};

/** Marketplace.vue resetRefinementList facets on a make change. */
const VEHICLE_INDEX_MAKE_DEPENDENT_FACETS = [
    "model",
    "specification",
    "options",
    "reference_exterior_color",
    "reference_interior_color",
];

/** True when a curated range (e.g. monthly_price_max) no longer applies. */
export const hasCuratedRangeChanged = (): boolean => {
    const config = curatedSearchConfig.value;
    if (!config.isActive || !config.filters) return false;
    const filters = config.filters;
    return RANGE_META.some(({ attribute, minKey, maxKey }) => {
        const curatedMin = minKey
            ? (filters[minKey] as number | undefined)
            : undefined;
        const curatedMax = filters[maxKey] as number | undefined;
        const range = rangeFiltersState.value[attribute];
        return (
            (curatedMin !== undefined && range.min !== curatedMin) ||
            (curatedMax !== undefined && range.max !== curatedMax)
        );
    });
};

/**
 * Blade equivalent of Marketplace.vue handleAlgoliaStateChange for curated SEO pages.
 */
export const createCuratedPageStateChangeHandler = (
    options: CuratedPageStateChangeOptions,
) => {
    const stateMapping = createStateMapping(options.indexName);
    const vehicleIndexParity = options.vehicleIndexParity === true;
    const menuMake = (state: UiState): string | undefined =>
        (state[options.indexName] as { menu?: { make?: string } } | undefined)
            ?.menu?.make;
    // Marketplace.vue previousSelectedMake: the curated make first.
    let previousMake: string | undefined =
        curatedSearchConfig.value.filters?.menu?.make;

    return ({
        uiState,
        setUiState,
    }: {
        uiState: UiState;
        setUiState: SetUiStateFn;
    }) => {
        initInstantSearchBridge(setUiState, uiState, options.indexName);

        const isUserFacetRefine = options.getHasPendingUserFacetRefine();
        options.clearHasPendingUserFacetRefine();

        const isSpecialPage =
            options.isCuratedSearchPage ||
            (curatedSearchConfig.value.isActive &&
                !!curatedSearchConfig.value.filters);

        if (
            options.shouldRedirectFiltersToMarketplace &&
            options.getIsCuratedReady() &&
            !options.isCuratedRedirectPending() &&
            isUserFacetRefine
        ) {
            const diff = stateMapping.stateToRoute(uiState);
            if (
                Object.keys(diff).length > 0 ||
                (vehicleIndexParity && hasCuratedRangeChanged())
            ) {
                options.setCuratedRedirectPending(true);
                suppressNextRouteWrite();
                window.location.href = uiStateToMarketplaceUrl(
                    uiState,
                    options.indexName,
                );
                return;
            }
        }

        const skipCuratedRestore =
            isUserFacetRefine ||
            (options.getUserFilterGraceActive?.() ?? false);

        if (isSpecialPage && !skipCuratedRestore) {
            const compareRouteStates =
                vehicleIndexParity && options.getIsCuratedReady();
            if (
                restoreCuratedUiStateIfDrifted(
                    uiState,
                    setUiState,
                    options.indexName,
                    compareRouteStates,
                )
            ) {
                if (vehicleIndexParity) {
                    previousMake =
                        parseUrlToRouteState(window.location.href).menu
                            ?.make ??
                        curatedSearchConfig.value.filters?.menu?.make;
                }
                return;
            }
        }

        if (vehicleIndexParity) {
            const currentMake = menuMake(uiState);
            if (previousMake !== currentMake) {
                resetRefinementListInUiState(
                    uiState,
                    options.indexName,
                    VEHICLE_INDEX_MAKE_DEPENDENT_FACETS,
                );
            }
            previousMake = currentMake;
        }

        setUiState(uiState);
    };
};

// === SUPPRESSION D'ÉCRITURE HISTORIQUE ===
// Quand on redirige vers /marketplace via window.location.href, le routeur Algolia
// fait un pushState sur la NOUVELLE page (pas l'ancienne) :
//   1. window.location.href navigue vers /marketplace?refinementList&price_min
//   2. La nouvelle page charge, InstantSearch s'initialise
//   3. Le routeur Algolia lit l'URL, fait stateToRoute → createURL
//   4. L'URL générée a un ordre de params différent (ex: price_min&refinementList)
//   5. Le routeur voit "URL différente" → pushState → DOUBLON dans l'historique
//
// Solution : persister le flag via sessionStorage pour qu'il survive au rechargement.
// Sur la nouvelle page, on le lit et on override pushState (one-shot) pour supprimer
// la normalisation d'URL initiale du routeur.

const SUPPRESS_KEY = "__suppress_next_pushstate";
let suppressSafetyTimer: ReturnType<typeof setTimeout> | null = null;
// Référence vers le pushState natif. Capturé paresseusement au premier appel
// de activatePushStateSuppress (pas au chargement du module, pour que les tests
// Node.js puissent mocker window.history avant). Le flag `isPushStateOverridden`
// empêche de recapturer la version patchée lors d'un double appel.
let nativePushState: typeof window.history.pushState | undefined;
let isPushStateOverridden = false;

const activatePushStateSuppress = () => {
    if (typeof window === "undefined") return;
    if (!isPushStateOverridden) {
        nativePushState = window.history.pushState.bind(window.history);
    }
    if (!nativePushState) return;

    isPushStateOverridden = true;

    const restore = () => {
        if (suppressSafetyTimer !== null) {
            clearTimeout(suppressSafetyTimer);
            suppressSafetyTimer = null;
        }
        isPushStateOverridden = false;
        window.history.pushState = nativePushState!;
    };

    (window.history as any).pushState = (
        _state: unknown,
        _title: string,
        _url?: string | URL | null,
    ) => {
        restore();
    };

    suppressSafetyTimer = setTimeout(restore, 2000);
};

// Sur chargement de page : si le flag sessionStorage est présent,
// activer le suppress pour bloquer le premier pushState du routeur Algolia.
if (typeof window !== "undefined") {
    try {
        if (sessionStorage.getItem(SUPPRESS_KEY)) {
            sessionStorage.removeItem(SUPPRESS_KEY);
            activatePushStateSuppress();
        }
    } catch {
        // sessionStorage indisponible (navigation privée, etc.)
    }
}

/**
 * À appeler juste avant window.location.href = marketplaceUrl.
 * 1. Override pushState sur la page courante (pour les timers en attente).
 * 2. Persiste le flag via sessionStorage pour la page suivante (rechargement complet).
 */
export const suppressNextRouteWrite = () => {
    if (typeof window === "undefined") return;

    // Persister pour la nouvelle page (survit au rechargement)
    try {
        sessionStorage.setItem(SUPPRESS_KEY, "1");
    } catch {
        // sessionStorage indisponible
    }

    // Aussi activer sur la page courante (pour les timers debounce en vol)
    activatePushStateSuppress();
};

// === ROUTER ===
const createRouter = () =>
    history({
        cleanUrlOnDispose: false,
        createURL({ qsModule, routeState, location }) {
            // Sur pages curées/landing (pathname != /marketplace), on garde l'URL
            // locale propre. Les facettes user sont transportées uniquement via
            // uiStateToMarketplaceUrl() lors du redirect explicite.
            if (isLandingExcerptPathname(location.pathname)) {
                const displayPage = instantSearchPageToLandingDisplayPage(
                    Number(routeState.page ?? 0),
                );
                if (displayPage > 1) {
                    const queryString = qsModule.stringify(
                        { page: displayPage },
                        {
                            addQueryPrefix: true,
                            encode: false,
                        },
                    );
                    return `${location.origin}${location.pathname}${queryString}`;
                }

                return `${location.origin}${location.pathname}`;
            }

            const queryString = qsModule.stringify(routeState, {
                addQueryPrefix: true,
                arrayFormat: "indices",
                encode: false,
            });
            return `${location.origin}${location.pathname}${queryString}`;
        },
        parseURL({ qsModule, location }): any {
            return qsModule.parse(location.search.slice(1), {
                arrayLimit: 100,
            });
        },
    });

// === FACTORY ===
export const createAlgoliaRouting = (indexName: string) => ({
    router: createRouter(),
    stateMapping: createStateMapping(indexName),
});

/**
 * Blade marketplace routing with `specification` URL param (rangeFiltersState parity with Vue).
 */
export const createMarketplaceBladeRouting = (indexName: string) => {
    const bladeRouting = createAlgoliaRoutingBlade(indexName);

    return {
        ...bladeRouting,
        stateMapping: {
            stateToRoute(uiState: UiState): Record<string, unknown> {
                const routeState =
                    bladeRouting.stateMapping.stateToRoute(uiState);

                // Non-refining ranges flow through `rangeFiltersState` (parity with
                // Vue `buildRouteState`), not InstantSearch's native `range` UI state.
                // Without this, the router rewrites the URL without the range params
                // right after our manual `syncRangeFiltersToUrl` adds them.
                const ranges = rangeFiltersState.value;
                for (const { attribute, minKey, maxKey } of RANGE_META) {
                    const range = ranges[attribute];
                    if (!range) continue;
                    if (minKey && range.min !== undefined)
                        routeState[minKey] = range.min;
                    if (range.max !== undefined) routeState[maxKey] = range.max;
                }

                const spec = ranges.specification?.trim();
                if (spec) {
                    routeState.specification = spec;
                }

                // Sans widget, InstantSearch ne connaît pas ces filtres : on les réécrit
                // pour qu'ils survivent aux mises à jour d'URL.
                const hidden = hiddenRefinementsState.value;
                if (Object.keys(hidden).length > 0) {
                    routeState.refinementList = {
                        ...((routeState.refinementList as
                            | Record<string, string[]>
                            | undefined) ?? {}),
                        ...hidden,
                    };
                }
                return routeState;
            },

            routeToState(incomingRouteState: Record<string, unknown>): UiState {
                const { hidden, rest } = extractHiddenRefinements(
                    incomingRouteState.refinementList,
                );
                hiddenRefinementsState.value = hidden;
                const routeState: Record<string, unknown> = {
                    ...incomingRouteState,
                };
                if (rest && Object.keys(rest).length > 0) {
                    routeState.refinementList = rest;
                } else {
                    delete routeState.refinementList;
                }

                // Hydrate `rangeFiltersState` from the URL so configure.filters and
                // the range inputs reflect the current params (initial load + popstate).
                syncRangeFiltersFromParsed(parseRouteStateFilters(routeState));
                mutateRangeFiltersState((state) => {
                    state.specification = routeState.specification
                        ? String(routeState.specification)
                        : "";
                });

                const uiState =
                    bladeRouting.stateMapping.routeToState(routeState);

                const filtersString = [
                    getFiltersFromRangeState(),
                    getHiddenRefinementsFilterString(),
                ]
                    .filter(Boolean)
                    .join(" && ");
                if (filtersString) {
                    const indexState = uiState[indexName] as Record<
                        string,
                        unknown
                    >;
                    const existing = (
                        indexState.configure as { filters?: string } | undefined
                    )?.filters;
                    indexState.configure = {
                        filters: existing
                            ? `${existing} && ${filtersString}`
                            : filtersString,
                    };
                }

                return uiState;
            },
        },
    };
};

/**
 * Routing for Blade curated-search pages (/curated-search-blade/{slug}).
 * Reuses curated merge logic from createStateMapping; keeps the SEO pathname clean (no query params).
 */
export const createAlgoliaRoutingBladeCurated = (indexName: string) => ({
    router: history({
        cleanUrlOnDispose: false,
        createURL({ location }) {
            return `${location.origin}${location.pathname}`;
        },
        parseURL({ qsModule, location }): any {
            return qsModule.parse(location.search.slice(1), {
                arrayLimit: 100,
            });
        },
    }),
    stateMapping: createStateMapping(indexName),
});
